"use client";

import { useSyncExternalStore } from "react";
import { motion } from "motion/react";
import styles from "./three-d-marquee.module.css";

type ThreeDMarqueeProps = {
  images: readonly string[];
  className?: string;
};

function subscribeReducedMotion(onStoreChange: () => void) {
  const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
  mq.addEventListener("change", onStoreChange);
  return () => mq.removeEventListener("change", onStoreChange);
}

function getReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const COLUMN_COUNT = 3;
/** Enough tiles per column that continuous scroll never opens a black gap. */
const MIN_TILES_PER_COLUMN = 12;
const MIN_TILES_PER_COLUMN_MOBILE = 6;

function subscribeMobile(onChange: () => void) {
  const mq = window.matchMedia("(max-width: 600px)");
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function getMobile() {
  return window.matchMedia("(max-width: 600px)").matches;
}

function repeatToFill(images: readonly string[], minTotal: number): string[] {
  if (images.length === 0) return [];
  const out: string[] = [];
  while (out.length < minTotal) {
    out.push(...images);
  }
  return out;
}

/**
 * Aceternity 3D Marquee — 3 columns, isometric grid.
 * Columns scroll in one direction only (seamless loop via duplicated stack).
 * @see https://ui.aceternity.com/components/3d-marquee
 */
export function ThreeDMarquee({ images, className }: ThreeDMarqueeProps) {
  const reduced = useSyncExternalStore(subscribeReducedMotion, getReducedMotion, () => false);
  const mobile = useSyncExternalStore(subscribeMobile, getMobile, () => false);
  const minTiles = mobile
    ? COLUMN_COUNT * MIN_TILES_PER_COLUMN_MOBILE
    : COLUMN_COUNT * MIN_TILES_PER_COLUMN;
  const filled = repeatToFill(images, minTiles);
  const chunkSize = Math.max(1, Math.ceil(filled.length / COLUMN_COUNT));
  const chunks = Array.from({ length: COLUMN_COUNT }, (_, colIndex) => {
    const start = colIndex * chunkSize;
    return filled.slice(start, start + chunkSize);
  });

  return (
    <div className={[styles.root, className].filter(Boolean).join(" ")}>
      <div className={styles.stage}>
        <div className={styles.plane}>
          <div className={styles.grid}>
            {chunks.map((column, colIndex) => {
              const upward = colIndex % 2 === 0;
              /* Duplicate stack so -50% / +50% loops seamlessly. */
              const loopColumn = [...column, ...column];
              return (
                <motion.div
                  key={`col-${colIndex}`}
                  className={styles.column}
                  animate={
                    reduced
                      ? undefined
                      : { y: upward ? ["0%", "-50%"] : ["-50%", "0%"] }
                  }
                  transition={
                    reduced
                      ? undefined
                      : {
                          duration: upward ? (mobile ? 96 : 78) : mobile ? 110 : 92,
                          repeat: Infinity,
                          ease: "linear",
                        }
                  }
                >
                  <span className={styles.vLine} aria-hidden />
                  {loopColumn.map((image, imageIndex) => (
                    <div
                      className={styles.cell}
                      key={`${image}-${colIndex}-${imageIndex}`}
                    >
                      <span className={styles.hLine} aria-hidden />
                      <motion.img
                        src={image}
                        alt=""
                        width={970}
                        height={700}
                        loading={imageIndex < 2 ? "eager" : "lazy"}
                        decoding="async"
                        className={styles.image}
                        whileHover={reduced || mobile ? undefined : { y: -10 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      />
                    </div>
                  ))}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
